import { Link } from '@inertiajs/react';

export interface BreadcrumbCrumb {
    name: string;
    url: string | null;
}

interface BreadcrumbTrailProps {
    crumbs: BreadcrumbCrumb[];
    className?: string;
}

/**
 * Quiet editorial breadcrumb trail for the public journal article page.
 *
 * Mirrors the server-built `breadcrumbs` prop 1:1 — the JSON-LD BreadcrumbList
 * is generated from the same array, so the visible trail and the markup never
 * diverge. Crumbs with `url: null` render as plain text (never invent a link);
 * the last crumb is the current page and gets `aria-current="page"`.
 */
export function BreadcrumbTrail({ crumbs, className = '' }: BreadcrumbTrailProps) {
    if (!crumbs || crumbs.length === 0) return null;

    const lastIndex = crumbs.length - 1;

    return (
        <nav aria-label="Breadcrumb" data-testid="article-breadcrumb" className={`min-w-0 overflow-hidden ${className}`.trim()}>
            <ol className="flex min-w-0 flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] tracking-[0.2em] uppercase">
                {crumbs.map((crumb, index) => {
                    const isCurrent = index === lastIndex;
                    return (
                        <li
                            key={`${index}-${crumb.name}`}
                            className={`flex min-w-0 items-center gap-x-2 ${isCurrent ? 'max-w-[16rem] sm:max-w-sm md:max-w-md' : ''}`.trim()}
                        >
                            {isCurrent ? (
                                <span aria-current="page" className="block truncate" style={{ color: 'rgba(14, 39, 39, 0.6)' }} title={crumb.name}>
                                    {crumb.name}
                                </span>
                            ) : crumb.url ? (
                                <Link
                                    href={crumb.url}
                                    className="shrink-0 underline-offset-4 transition-opacity hover:underline hover:opacity-80"
                                    style={{ color: 'rgba(14, 39, 39, 0.45)' }}
                                >
                                    {crumb.name}
                                </Link>
                            ) : (
                                <span className="shrink-0" style={{ color: 'rgba(14, 39, 39, 0.45)' }}>
                                    {crumb.name}
                                </span>
                            )}
                            {!isCurrent && (
                                <span aria-hidden="true" className="shrink-0 select-none" style={{ color: 'rgba(14, 39, 39, 0.3)' }}>
                                    ›
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}
